<template>
  <div>
    <div>
      <div
        v-if="showHeader"
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="
            !filterOrganization &&
            !filterInvoice &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <base-input
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOCATIONS)"
          :placeholder="`${$t('COMMON.LOCATIONS')}`"
        >
          <locations-selector
            @locationsChanged="(locations) => (selectedLocations = locations)"
          />
        </base-input>

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.DOCUMENT')"
            clearable
          />
        </div>
      </div>
      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="salesPayments"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>
        <el-table-column
          :label="$t('COMMON.NUMBER')"
          min-width="250px"
          sortable="custom"
          prop="seq_number"
        >
          <template slot-scope="{ row }">
            {{ row.seq_number ?? row.code }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.DATE')"
          prop="date"
          sortable="date"
          min-width="250px"
        >
          <template v-slot="{ row }">
            {{ $timeZoneDateFormat(row.date) }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.STATUS')"
          prop="status"
          min-width="150px"
        >
          <template v-slot="{ row }">
            <salesPayment-status-badge :salesPayment="row" />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.AMOUNT')"
          prop="amount"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row.amount) }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.LOCATIONS')"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOCATIONS)"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <locations :locations="row.allowedLocations" />
          </template>
        </el-table-column>

        <el-table-column
          v-if="
            !filterOrganization &&
            !filterInvoice &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          :label="$t('COMMON.ORGANIZATION')"
          prop="organization"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>

        <el-table-column
          v-if="!filterInvoice"
          :label="$t('COMMON.INVOICE')"
          prop="invoice.code"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <router-link
              :to="{
                name: 'View SalesInvoice',
                params: { id: row.invoice.id },
              }"
            >
              {{ row.invoice ? row.invoice.code : null }}
            </router-link>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.CREATED_AT')"
          prop="created_at"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ $timeZoneDateFormat(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column min-width="100px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_SALES_PAYMENTS)"
            >
              <a
                type="text"
                @click="viewSalesPayment(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      v-if="showHeader"
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import "sweetalert2/dist/sweetalert2.css";
import { INVOICE_STATUS_SENT } from "@/constants/invoices";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import LocationsSelector from "@/components/LocationsSelector.vue";
import SalesPaymentStatusBadge from "./SalesPaymentStatusBadge.vue";

export default {
  name: "salesPayment-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    SalesPaymentStatusBadge,
    LocationsSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterInvoice: {
      type: String,
      default: null,
      description: "Invoice id",
    },
    filterServiceCenter: {
      type: String,
      default: null,
      description: "Service center id",
    },
    filterEstablishment: {
      type: String,
      default: null,
      description: "Establishment id",
    },
    filterCafeteria: {
      type: String,
      default: null,
      description: "Cafeteria id",
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-date",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      salesPayments: [],
      INVOICE_STATUS_SENT: INVOICE_STATUS_SENT,
      loading: true,
      selectedOrganization: null,
      selectedLocations: null,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getList",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getList",
      immediate: true,
    },
    filterInvoice: {
      handler: "getList",
      immediate: true,
    },
    selectedLocations: {
      handler: "getList",
      immediate: true,
    },
    filterServiceCenter: {
      handler: "getList",
      immediate: true,
    },
    filterEstablishment: {
      handler: "getList",
      immediate: true,
    },
    filterCafeteria: {
      handler: "getList",
      immediate: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      this.loading = true;
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,invoice,allowedLocations",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.filterInvoice) {
          params = {
            ...params,
            filter: { ...params.filter, invoice: this.filterInvoice },
          };
        }
        if (this.selectedLocations) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              allowedLocations: this.selectedLocations.map((loc) => loc.id),
            },
          };
        }
        if (this.filterServiceCenter) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              serviceCenter: this.filterServiceCenter,
            },
          };
        }
        if (this.filterEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.filterEstablishment,
            },
          };
        }
        if (this.filterCafeteria) {
          params = {
            ...params,
            filter: { ...params.filter, cafeteria: this.filterCafeteria },
          };
        }

        await this.$store.dispatch("salesPayments/list", params);
        this.salesPayments = this.$store.getters["salesPayments/list"];
        this.total = this.$store.getters["salesPayments/listTotal"];
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewSalesPayment(salesPayment) {
      this.$router.push({
        name: "View SalesPayment",
        params: { id: salesPayment.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
